import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useScenarioSessionStore } from "../stores/scenarioSessionStore";
import { TranscriptTurn, ScenarioInfo } from "../services/api";
import { scenarios } from "../services/scenarios";
import useSession from "./useSession";
import useSaveTranscript from "./useSaveTranscript";
import useUploadAudio from "./useUploadAudio";
import SpeechService from "../services/SpeechService";
import ChatService from "../services/ChatService";
import { convertAudioToWav } from "../utils/audio";

/**
 * Controller hook that keeps React Query as the source of truth for transcripts,
 * while Zustand only owns lightweight UI/session state.
 */
export function useScenarioSession() {
  const {
    sessionId,
    currentScenario,
    isListening,
    isProcessing,
    isSpeaking,
    isInitialized,
    isConversationFinished,
    error,
    setSessionId,
    setScenario,
    setError,
    setIsInitialized,
    setIsListening,
    setIsProcessing,
    setIsSpeaking,
    setIsConversationFinished,
  } = useScenarioSessionStore();

  // Local state for retry handling
  const [chatRetryState, setChatRetryState] = useState<{
    isRetryMode: boolean;
    hasRetried: boolean;
    lastUserMessage?: string;
    lastUserAudioBlob?: Blob | null;
  }>({ isRetryMode: false, hasRetried: false });

  const speechServiceRef = useRef<SpeechService | null>(null);
  const chatServiceRef = useRef<ChatService | null>(null);

  const {
    data: serverTranscript,
    isLoading: isLoadingTranscript,
    error: transcriptError,
  } = useSession(sessionId);

  const saveTranscriptMutation = useSaveTranscript({
    enableOptimisticUpdates: true,
  });
  const { uploadAudioAsync, ...uploadAudioMutation } = useUploadAudio();

  // Initialize services once
  useEffect(() => {
    let isCancelled = false;

    const initServices = async () => {
      try {
        setError(null);
        const speechService = new SpeechService();
        await speechService.initialize();
        
        // Set voice name immediately after initialization
        speechService.setVoiceName(currentScenario.voiceName);

        const chatService = new ChatService({ scenario: currentScenario });

        if (!isCancelled) {
          speechServiceRef.current = speechService;
          chatServiceRef.current = chatService;
          setIsInitialized(true);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(
            "Fout bij initialiseren van services: " + (err as Error).message
          );
        }
      }
    };

    void initServices();

    return () => {
      isCancelled = true;
      speechServiceRef.current?.cleanup();
      speechServiceRef.current = null;
      chatServiceRef.current?.clearConversation();
      chatServiceRef.current = null;
      setIsInitialized(false);
    };
  }, [currentScenario, setError, setIsInitialized]);

  // Keep chat service scenario in sync and set voice name
  useEffect(() => {
    chatServiceRef.current?.setScenario(currentScenario);
    speechServiceRef.current?.setVoiceName(currentScenario.voiceName);
  }, [currentScenario]);

  // Load conversation history into chat service when transcript changes
  useEffect(() => {
    if (serverTranscript && chatServiceRef.current) {
      chatServiceRef.current.loadConversationHistory(
        serverTranscript.turns.map((turn) => ({
          role: turn.role,
          content: turn.content,
        }))
      );
    }
  }, [serverTranscript]);

  // Restore scenario from server transcript if available
  useEffect(() => {
    if (serverTranscript?.scenario) {
      const storedScenario = scenarios[serverTranscript.scenario.id];
      if (storedScenario && storedScenario.id !== currentScenario.id) {
        setScenario(storedScenario);
      }
    }
  }, [serverTranscript, currentScenario.id, setScenario]);

  // Handle upload errors
  useEffect(() => {
    if (uploadAudioMutation.error) {
      const errorMessage =
        uploadAudioMutation.error instanceof Error
          ? uploadAudioMutation.error.message
          : String(uploadAudioMutation.error);
      setError(`Audio upload failed: ${errorMessage}`);
    }
  }, [uploadAudioMutation.error, setError]);

  // Handle transcript fetch errors (ignore 404s)
  useEffect(() => {
    if (transcriptError) {
      if (
        transcriptError &&
        typeof transcriptError === "object" &&
        "response" in transcriptError &&
        (transcriptError as any).response?.status === 404
      ) {
        return;
      }
      setError(`Failed to load transcript: ${transcriptError.message}`);
    }
  }, [transcriptError, setError]);

  const uploadAudio = useCallback(
    async (audio: Blob, fileName: string): Promise<string | undefined> => {
      try {
        const result = await uploadAudioAsync({
          sessionId,
          audio,
          fileName,
        });
        return result.blobUrl;
      } catch (err) {
        console.error("Audio upload failed:", err);
        return undefined;
      }
    },
    [sessionId, uploadAudioAsync]
  );

  const persistTurns = useCallback(
    (turns: TranscriptTurn[]) => {
      const scenarioInfo: ScenarioInfo = {
        id: currentScenario.id,
        title: currentScenario.title,
      };
      saveTranscriptMutation.mutate({ sessionId, turns, scenario: scenarioInfo });
    },
    [saveTranscriptMutation, sessionId, currentScenario]
  );

  const processUserMessage = useCallback(
    async (userMessage: string, userAudioBlob: Blob | null) => {
      if (!chatServiceRef.current) {
        setError("Chat service niet beschikbaar");
        return;
      }

      setIsProcessing(true);
      const baseTurns = serverTranscript?.turns ?? [];

      try {
        const now = new Date();
        const userTurn: TranscriptTurn = {
          role: "user",
          content: userMessage,
          timestamp: now.toISOString(),
        };

        if (userAudioBlob) {
          const fileName = `user-${now
            .toISOString()
            .replace(/[:.]/g, "-")}.wav`;
          userTurn.audioBlobUrl = await uploadAudio(userAudioBlob, fileName);
        }

        const turnsAfterUser = [...baseTurns, userTurn];
        persistTurns(turnsAfterUser);

        // Keep chat history aligned with latest transcript
        chatServiceRef.current.loadConversationHistory(
          turnsAfterUser.map((turn) => ({
            role: turn.role,
            content: turn.content,
          }))
        );

        let botResponse: string;
        try {
          botResponse = await chatServiceRef.current.getChatResponse(
            userMessage
          );
        } catch (chatError) {
          console.error("Chat API error:", chatError);
          
          // Update the user turn with error information
          const errorUserTurn: TranscriptTurn = {
            ...userTurn,
            error: {
              type: 'api_error',
              message: 'Chat API fout - geen response ontvangen',
              details: chatError instanceof Error ? chatError.message : String(chatError)
            }
          };
          
          const turnsWithError = [...baseTurns, errorUserTurn];
          persistTurns(turnsWithError);
          
          setChatRetryState({
            isRetryMode: true,
            hasRetried: false,
            lastUserMessage: userMessage,
            lastUserAudioBlob: userAudioBlob
          });
          setError("Sorry er ging iets mis.");
          setIsProcessing(false);
          return;
        }

        // Check if conversation is finished (contains STATE_FINISHED)
        if (botResponse.includes("STATE_FINISHED")) {
          console.log("Gesprek is afgerond, verwerken afscheidsboodschap");
          
          // Extract farewell message (everything before STATE_FINISHED)
          const farewellMessage = botResponse.replace(/\n\nSTATE_FINISHED$/, "").trim();
          
          if (farewellMessage) {
            const assistantTimestamp = new Date();
            const farewellTurn: TranscriptTurn = {
              role: "assistant",
              content: farewellMessage,
              timestamp: assistantTimestamp.toISOString(),
            };

            // Speak the farewell message
            const speechService = speechServiceRef.current;
            if (speechService) {
              try {
                setIsProcessing(false); // Stop "thinking" when we start speaking
                setIsSpeaking(true);
                const speakResult = await speechService.speak(farewellMessage);
                if (speakResult.audioBlob) {
                  let wavBlob = speakResult.audioBlob;
                  try {
                    wavBlob = await convertAudioToWav(speakResult.audioBlob);
                  } catch (conversionError) {
                    console.warn(
                      "Kon farewell audio niet converteren, gebruikt origineel:",
                      conversionError
                    );
                  }

                  const fileName = `assistant-${assistantTimestamp
                    .toISOString()
                    .replace(/[:.]/g, "-")}.wav`;
                  farewellTurn.audioBlobUrl = await uploadAudio(wavBlob, fileName);
                }
              } catch (err) {
                console.warn("Farewell audio upload mislukt:", err);
              } finally {
                setIsSpeaking(false);
              }
            }

            const finalTurns = [...turnsAfterUser, farewellTurn];
            persistTurns(finalTurns);
          }
          
          setIsProcessing(false);
          setIsConversationFinished(true);
          return;
        }

        const assistantTimestamp = new Date();
        const assistantTurn: TranscriptTurn = {
          role: "assistant",
          content: botResponse,
          timestamp: assistantTimestamp.toISOString(),
        };

        const speechService = speechServiceRef.current;
        if (speechService) {
          try {
            setIsProcessing(false); // Stop "thinking" when we start speaking
            setIsSpeaking(true);
            const speakResult = await speechService.speak(botResponse);
            if (speakResult.audioBlob) {
              let wavBlob = speakResult.audioBlob;
              try {
                wavBlob = await convertAudioToWav(speakResult.audioBlob);
              } catch (conversionError) {
                console.warn(
                  "Kon assistant audio niet converteren, gebruikt origineel:",
                  conversionError
                );
              }

              const fileName = `assistant-${assistantTimestamp
                .toISOString()
                .replace(/[:.]/g, "-")}.wav`;
              assistantTurn.audioBlobUrl = await uploadAudio(wavBlob, fileName);
            }
          } catch (err) {
            console.warn("Assistant audio upload mislukt:", err);
          } finally {
            setIsSpeaking(false);
          }
        } else {
          // If no speech service, just stop processing
          setIsProcessing(false);
        }

        const finalTurns = [...turnsAfterUser, assistantTurn];
        persistTurns(finalTurns);
      } catch (err) {
        console.error("Processing error:", err);
        setError("Verwerkingsfout: " + (err as Error).message);
      } finally {
        // Only set processing to false if it hasn't been set to false already
        setIsProcessing(false);
      }
    },
    [setIsProcessing, serverTranscript?.turns, setError, persistTurns, uploadAudio, setIsSpeaking, setIsConversationFinished]
  );

  const startListening = useCallback(async () => {
    if (isListening || isProcessing || !isInitialized) {
      return;
    }

    const speechService = speechServiceRef.current;
    if (!speechService) {
      setError("Speech service niet beschikbaar");
      return;
    }

    setIsListening(true);
    setError(null);

    try {
      const result = await speechService.startListening();
      // Set listening to false when speech recognition is complete
      setIsListening(false);
      
      if (result.transcript && result.transcript.trim()) {
        // processUserMessage will handle setting isProcessing to true
        await processUserMessage(result.transcript, result.audioBlob);
      } else {
        console.log("Geen spraak gedetecteerd of lege transcript");
      }
    } catch (err) {
      console.error("Speech recognition error:", err);
      setError("Speech recognition error: " + (err as Error).message);
      setIsListening(false);
    }
  }, [
    isInitialized,
    isListening,
    isProcessing,
    processUserMessage,
    setError,
    setIsListening,
  ]);

  const stopListening = useCallback(async () => {
    if (!isListening || !isInitialized) {
      return;
    }

    const speechService = speechServiceRef.current;
    if (!speechService) {
      setError("Speech service niet beschikbaar");
      return;
    }

    try {
      await speechService.stopListening();
      // Note: setIsListening(false) will be called when the listening promise resolves
    } catch (err) {
      console.error("Error stopping speech recognition:", err);
      setError("Fout bij stoppen spraakherkenning: " + (err as Error).message);
      setIsListening(false);
    }
  }, [isInitialized, isListening, setError, setIsListening]);

  const clearChat = useCallback(() => {
    chatServiceRef.current?.clearConversation();
    setIsConversationFinished(false);
    persistTurns([]);
    setChatRetryState({ isRetryMode: false, hasRetried: false });
  }, [persistTurns, setIsConversationFinished]);

  const retryLastMessage = useCallback(async () => {
    if (!chatRetryState.isRetryMode || !chatRetryState.lastUserMessage || chatRetryState.hasRetried) {
      return;
    }
    
    setChatRetryState(prev => ({ ...prev, hasRetried: true }));
    setError(null);
    await processUserMessage(chatRetryState.lastUserMessage, chatRetryState.lastUserAudioBlob ?? null);
  }, [chatRetryState, processUserMessage, setError]);

  const askNewQuestion = useCallback(() => {
    setChatRetryState({ isRetryMode: false, hasRetried: false });
    setError(null);
    // User can now speak again
  }, [setError]);

  const speakOpeningMessage = useCallback(async () => {
    if (!isInitialized || !currentScenario) {
      return;
    }

    const speechService = speechServiceRef.current;
    if (!speechService) {
      setError("Speech service niet beschikbaar");
      return;
    }

    try {
      setError(null);
      setIsSpeaking(true);
      
      // Add opening message to transcript
      const baseTurns = serverTranscript?.turns ?? [];
      const now = new Date();
      const openingTurn: TranscriptTurn = {
        role: "assistant",
        content: currentScenario.openingMessage,
        timestamp: now.toISOString(),
      };

      // Speak the opening message
      const speakResult = await speechService.speak(currentScenario.openingMessage);
      
      // Add audio blob if available
      if (speakResult.audioBlob) {
        try {
          let wavBlob = speakResult.audioBlob;
          try {
            wavBlob = await convertAudioToWav(speakResult.audioBlob);
          } catch (conversionError) {
            console.warn("Kon opening audio niet converteren, gebruikt origineel:", conversionError);
          }

          const fileName = `opening-${now.toISOString().replace(/[:.]/g, "-")}.wav`;
          openingTurn.audioBlobUrl = await uploadAudio(wavBlob, fileName);
        } catch (err) {
          console.warn("Opening audio upload mislukt:", err);
        }
      }

      // Save opening message to transcript
      const turnsWithOpening = [...baseTurns, openingTurn];
      persistTurns(turnsWithOpening);
      
    } catch (err) {
      console.error("Error speaking opening message:", err);
      setError("Kon openingsbericht niet uitspreken: " + (err as Error).message);
    } finally {
      setIsSpeaking(false);
    }
  }, [isInitialized, currentScenario, setError, setIsSpeaking, serverTranscript?.turns, persistTurns, uploadAudio]);

  const messages = useMemo(() => {
    return (serverTranscript?.turns ?? []).map((turn, index) => ({
      id: Date.parse(turn.timestamp) || Date.now() + index,
      type: turn.role as "user" | "assistant",
      content: turn.content,
      timestamp: turn.timestamp,
      audioBlobUrl: turn.audioBlobUrl,
    }));
  }, [serverTranscript?.turns]);

  const replayLastMessage = useCallback(async () => {
    if (!isInitialized || isProcessing || isListening || isSpeaking) {
      return;
    }

    // Find the last assistant message
    const lastAssistantMessage = messages
      .slice()
      .reverse()
      .find(msg => msg.type === 'assistant');

    if (!lastAssistantMessage || !lastAssistantMessage.content) {
      return;
    }

    const speechService = speechServiceRef.current;
    if (!speechService) {
      setError("Speech service niet beschikbaar");
      return;
    }

    try {
      setError(null);
      setIsSpeaking(true);
      await speechService.speak(lastAssistantMessage.content);
    } catch (err) {
      console.error("Error replaying message:", err);
      setError("Kon bericht niet opnieuw afspelen: " + (err as Error).message);
    } finally {
      setIsSpeaking(false);
    }
  }, [isInitialized, isProcessing, isListening, isSpeaking, messages, setError, setIsSpeaking]);

  // Helper to determine if conversation is in progress and user can continue
  const canContinueConversation = useMemo(() => {
    if (!isInitialized || isConversationFinished || messages.length === 0) {
      return false;
    }
    
    // Check if there's at least one assistant message (conversation has started)
    const hasAssistantMessage = messages.some(msg => msg.type === 'assistant');
    return hasAssistantMessage;
  }, [isInitialized, isConversationFinished, messages]);

  return {
    // Derived from server transcript
    messages,
    isLoadingTranscript,
    transcriptError,
    canContinueConversation,

    // Zustand state
    sessionId,
    currentScenario,
    isInitialized,
    isListening,
    isProcessing,
    isSpeaking,
    isConversationFinished,
    error,

    // Actions
    setSessionId,
    setScenario,
    setError,
    startListening,
    stopListening,
    clearChat,
    processUserMessage,
    speakOpeningMessage,
    
    // Retry functionality
    chatRetryState,
    retryLastMessage,
    askNewQuestion,
    replayLastMessage,
  };
}
